import { supabase } from './supabaseClient';

/**
 * Returns the teachers.id (UUID primary key) for the signed-in user — NOT
 * the same as auth_user_id. subscriptions/payment_methods/billing_transactions
 * all key off teachers.id, so every call below needs this rather than
 * supabaseClient's getCurrentUser() (which remaps .id to auth_user_id for
 * unrelated legacy reasons).
 */
export async function getMyTeacherId() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('teachers')
    .select('id')
    .eq('auth_user_id', user.id)
    .maybeSingle();
  if (error || !data) return null;
  return data.id;
}

export async function getCurrentSubscription(teacherId) {
  if (!teacherId) return null;
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*, plan:subscription_plans(*)')
    .eq('teacher_id', teacherId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function getPlans() {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('price_intl_usd_cents', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function getBillingHistory(teacherId) {
  if (!teacherId) return [];
  const { data, error } = await supabase
    .from('billing_transactions')
    .select('*')
    .eq('teacher_id', teacherId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Calls the create-checkout-session Edge Function, which talks to Stripe
 * with the secret key (never exposed to the browser) and returns a hosted
 * Checkout URL to redirect to. Only the international ('intl') rail is
 * wired up so far — the Russian rail's own create-*-payment function gets
 * added once a Russian legal entity exists to register a merchant account
 * under.
 */
export async function createCheckoutSession(planCode) {
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { plan_code: planCode },
  });
  if (error) throw error;
  if (!data?.url) throw new Error('No checkout URL returned');
  return data.url;
}

/**
 * Cancellation also goes through an Edge Function rather than a direct
 * table update — RLS deliberately has no UPDATE policy for authenticated
 * users on subscriptions, so a teacher can't grant themselves any status
 * by writing to it directly.
 */
export async function cancelSubscription() {
  const { data, error } = await supabase.functions.invoke('cancel-subscription', {});
  if (error) throw error;
  return data;
}
